import { createContext, useContext, useEffect, useMemo, useState, type ReactNode } from "react";
import { useLocation } from "react-router-dom";
import { useAuth } from "@/hooks/useAuth";
import {
	initTracker,
	trackPageView,
	trackEvent,
	trackConversion,
	identifyUser,
	getVisitorId,
	getSessionId,
} from "@/lib/tracker";

const ADMIN_PATH_PREFIX = "/manage";

interface TrackingContextValue {
	trackPageView: (path: string, title: string) => void;
	trackEvent: (name: string, params?: Record<string, unknown>) => void;
	trackConversion: (name: string, params?: Record<string, unknown>) => void;
	identifyUser: (userId: string) => void;
	getVisitorId: () => string;
	getSessionId: () => string;
}

const TrackingContext = createContext<TrackingContextValue | null>(null);

export function useTrackingContext(): TrackingContextValue {
	const ctx = useContext(TrackingContext);
	if (!ctx) {
		return {
			trackPageView: () => {},
			trackEvent: () => {},
			trackConversion: () => {},
			identifyUser: () => {},
			getVisitorId: () => "",
			getSessionId: () => "",
		};
	}
	return ctx;
}

interface TrackingProviderProps {
	children: ReactNode;
}

export function TrackingProvider({ children }: TrackingProviderProps) {
	const location = useLocation();
	const { isSignedIn, userId, session, getToken } = useAuth();
	const [authReady, setAuthReady] = useState(false);

	useEffect(() => {
		if (session !== undefined) {
			setAuthReady(true);
		}
	}, [session]);

	const userRole = session?.data?.user?.role;
	const isAdminUser = userRole === "admin";
	const isAdminPath = location.pathname.startsWith(ADMIN_PATH_PREFIX);
	const shouldTrack = authReady && !isAdminUser && !isAdminPath;

	// Initialize tracker on mount (skip for admin users/paths).
	useEffect(() => {
		if (shouldTrack) {
			initTracker();
		}
	}, [shouldTrack]);

	// Track page views on route change (skip admin areas).
	useEffect(() => {
		if (shouldTrack && location.pathname) {
			trackPageView(location.pathname, document.title);
		}
	}, [location.pathname, shouldTrack]);

	// Auto-identify user on login (skip admin users).
	useEffect(() => {
		if (shouldTrack && isSignedIn && userId) {
			getToken().then((token) => {
				identifyUser(userId, token ?? undefined);
			});
		}
	}, [isSignedIn, userId, shouldTrack, getToken]);

	const value = useMemo<TrackingContextValue>(
		() => ({
			trackPageView,
			trackEvent,
			trackConversion,
			identifyUser,
			getVisitorId,
			getSessionId,
		}),
		[],
	);

	return (
		<TrackingContext.Provider value={value}>
			{children}
		</TrackingContext.Provider>
	);
}
